import { Paper, Tab, Tabs, useTheme } from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import React, { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import { Loading, usePermissions, useTranslate } from "react-admin";

import { useGridUserDataGlobalFiltersContext } from "../../contexts/GridUserDataGlobalFiltersProvider";
import { UserRole } from "../../entities";
import { EditorTab } from "./components/EditorTab";
import { InventorizationTab } from "./components/InventorizationTab";
import { ViewTab } from "./components/ViewTab";
import { TabPanel, TabValue } from "./components/TabPanel";
import { useParseUrlHighlightData } from "./hooks/useParseUrlHighlightData";
import { useTerminalConfigErrorNotifications } from "./hooks/useTerminalConfigErrorNotifications";
import { useTerminalConfigLoader } from "./hooks/useTerminalConfigLoader";
import { useTerminalUrlSync } from "./hooks/useTerminalUrlSync";
import type { ViewState } from "./shared/types";

function StorageUIApp() {
  const theme = useTheme();
  const { permissions } = usePermissions();
  const translate = useTranslate();
  const [activeTab, setActiveTab] = useState<TabValue>(TabValue.view);

  const isAdmin = permissions && permissions.role === UserRole.admin;

  // Shared view state for both tabs
  const [viewState, setViewState] = useState<ViewState>({
    offsetX: 0,
    offsetY: 0,
    scale: 1,
    rotation: 0,
  });

  const [hasPersistedView, setHasPersistedView] = useState(false);

  const { data: globalTerminalId = {}, updateData: setGlobalTerminalId } =
    useGridUserDataGlobalFiltersContext();
  const terminalId = globalTerminalId?.terminal_id?.[0];

  const storageKey = typeof terminalId === 'number' ? `storage_ui_view_${terminalId}` : null;

  // Load saved view state when terminalId becomes available
  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setViewState(prev => ({
          offsetX: typeof parsed.offsetX === 'number' ? parsed.offsetX : prev.offsetX,
          offsetY: typeof parsed.offsetY === 'number' ? parsed.offsetY : prev.offsetY,
          scale: typeof parsed.scale === 'number' ? parsed.scale : prev.scale,
          rotation: typeof parsed.rotation === 'number' ? parsed.rotation : prev.rotation,
        }));
        // Mark that we loaded a persisted view so children can avoid auto-fitting
        setHasPersistedView(true);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse saved viewState', err);
    }
  }, [storageKey]);

  const debouncedSave = useCallback(
    _.debounce((key: string | null, serialized: string) => {
      if (!key) return;
      try {
        localStorage.setItem(key, serialized);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('Failed to save viewState', err);
      }
    }, 200),
    []
  );

  useEffect(() => {
    if (!storageKey) return;
    const serialized = JSON.stringify({
      offsetX: viewState.offsetX,
      offsetY: viewState.offsetY,
      scale: viewState.scale,
      rotation: viewState.rotation,
    });
    debouncedSave(storageKey, serialized);
  }, [viewState.offsetX, viewState.offsetY, viewState.scale, viewState.rotation, storageKey, debouncedSave]);


  useEffect(() => {
      return () => {
        debouncedSave.cancel();
      };
    }, [debouncedSave]);

  const { urlSelectedPlaceIds, urlSelectedTerminalId } = useParseUrlHighlightData();

  const hasTerminalMismatch = useTerminalUrlSync(urlSelectedTerminalId, terminalId, setGlobalTerminalId);

  useTerminalConfigErrorNotifications();

  const isLoading = useTerminalConfigLoader(terminalId, hasTerminalMismatch);

  if (typeof terminalId !== "number") {
    return <Loading />;
  }

  if (hasTerminalMismatch) {
    return <Loading />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Paper
      elevation={3}
      style={{
        position: "relative",
        width: "100%",
        flex: 1,
        minHeight: 0,
        margin: 0,
        padding: 0,
        background: theme.palette.storageUi.backgroundPrimary,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{`
        /* Override React Admin layout styles to use flexbox */
        main {
          display: flex !important;
          flex-direction: row !important;
          height: calc(100vh - 69px) !important; /* Adjust for app bar height */
          overflow: hidden !important;
        }
        #main-content {
          padding: 0 !important;
          margin: 0 !important;
          margin-top: 0 !important;
          flex: 1 !important;
          min-height: 0 !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
        }

      `}</style>
      <style>{`
        :root {
          --occupied-color: ${theme.palette.storageUi.occupied};
          --empty-color: ${theme.palette.storageUi.empty};
        }
        .highlight { color: ${theme.palette.storageUi.highlight} !important; font-weight: bold; }
        .occupied { color: var(--occupied-color); background-color: var(--occupied-color); }
        .blocked { color: var(--blocked-color); background-color: var(--blocked-color); }
        .empty { color: var(--empty-color); background-color: var(--empty-color); }
        .area-stats { margin: 15px 0; padding: 12px; border: 1px solid ${
          theme.palette.storageUi.backgroundPrimary
        }; background: ${alpha(theme.palette.secondary.main, 0.05)}; border-radius: 4px; }
        .place-details { margin: 15px 0; padding: 12px; border: 1px solid ${
          theme.palette.storageUi.borderLight
        }; background: ${alpha(theme.palette.secondary.main, 0.05)}; border-radius: 4px; }
        .level-filter { margin: 15px 0; padding: 10px; border: 1px solid ${
          theme.palette.storageUi.backgroundSecondary
        }; background: ${alpha(theme.palette.secondary.main, 0.05)}; border-radius: 4px; }
        .level-selector { margin: 10px 0; display: flex; gap: 5px; flex-wrap: wrap; }
        .level-btn { background: ${theme.palette.storageUi.backgroundSecondary}; color: ${
        theme.palette.secondary.main
      }; border: 1px solid ${
        theme.palette.secondary.main
      }; padding: 5px 10px; cursor: pointer; font-size: 11px; transition: all 0.2s; }
        .level-btn.active { background: ${theme.palette.secondary.main}; color: ${
        theme.palette.storageUi.textOnSecondary
      }; }
        .level-btn:hover { background: ${theme.palette.storageUi.buttonHover}; }
        .level-btn.active:hover { background: ${theme.palette.secondary.dark}; }
        .level-control-buttons { display: flex; gap: 5px; margin-top: 8px; }
        .level-control-btn { background: ${theme.palette.storageUi.backgroundSecondary}; color: ${
        theme.palette.secondary.main
      }; border: 1px solid ${
        theme.palette.secondary.main
      }; padding: 3px 8px; cursor: pointer; font-size: 10px; transition: all 0.2s; }
        .level-control-btn:hover { background: ${theme.palette.storageUi.buttonHover}; }
        .zoom-controls { position: absolute; top: 10px; right: 10px; display: flex; flex-direction: column; gap: 5px; z-index: 100; }
        .zoom-btn:hover { background: ${alpha(
          theme.palette.secondary.main,
          0.1
        )}; transform: scale(1.1); }
        h3, h4 { margin-top: 0; color: ${theme.palette.secondary.main}; }
        .legend-item { display: flex; align-items: center; gap: 8px; margin: 5px 0; }
        .legend-color { width: 12px; height: 12px; border: 1px solid ${
          theme.palette.storageUi.backgroundSecondary
        }; }
        .level-legend { display: flex; align-items: center; gap: 4px; margin: 3px 0; }
      `}</style>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_event, newValue) => setActiveTab(newValue)}
        indicatorColor="secondary"
        textColor="secondary"
        variant="fullWidth"
        aria-label="terminal config tabs"
        style={{
          background: theme.palette.storageUi.backgroundSecondary,
          borderBottom: `2px solid ${theme.palette.secondary.main}`,
        }}
      >
        {isAdmin && <Tab label={translate(`osfc.enums.tab_value.${TabValue[TabValue.editor]}`)} value={TabValue.editor} />}
        <Tab label={translate(`osfc.enums.tab_value.${TabValue[TabValue.view]}`)} value={TabValue.view} />
        <Tab label={translate(`osfc.enums.tab_value.${TabValue[TabValue.inventorization]}`)} value={TabValue.inventorization} />
      </Tabs>

      {/* zoom controls placeholder kept for layout consistency */}

      {/* Main */}
      <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {isAdmin && (
          <TabPanel value={activeTab} index={TabValue.editor}>
            <EditorTab viewState={viewState} setViewState={setViewState} />
          </TabPanel>
        )}

        <TabPanel value={activeTab} index={TabValue.view}>
          <ViewTab
              viewState={viewState}
              setViewState={setViewState}
              onRequestEditor={() => setActiveTab(TabValue.editor)}
              urlSelectedPlaceIds={urlSelectedPlaceIds}
              hasPersistedView={hasPersistedView}
            />
        </TabPanel>

        <TabPanel value={activeTab} index={TabValue.inventorization}>
          <InventorizationTab
            viewState={viewState}
            setViewState={setViewState}
            onRequestEditor={() => setActiveTab(TabValue.editor)}
            urlSelectedPlaceIds={urlSelectedPlaceIds}
            hasPersistedView={hasPersistedView}
          />
        </TabPanel>
      </div>
    </Paper>
  );
}

export default StorageUIApp;